import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import {
  cleanAuthCallbackUrl,
  getAuthCallbackError,
  getAuthCallbackType,
  hasAuthCallbackParams,
} from '../lib/authCallback';

/** Confirmation / recovery links must match Supabase Auth → URL Configuration allow-list. */
const authRedirectBase = () => `${window.location.origin}/`;

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  authNotice: string | null;
  needsPasswordReset: boolean;
  clearAuthNotice: () => void;
  clearPasswordReset: () => void;
  signUp: (
    email: string,
    password: string
  ) => Promise<{ error: AuthError | null; session: Session | null; user: User | null }>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  resendConfirmation: (email: string) => Promise<{ error: AuthError | null }>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [authNotice, setAuthNotice] = useState<string | null>(null);
  const [needsPasswordReset, setNeedsPasswordReset] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      const callbackError = getAuthCallbackError();
      if (callbackError) {
        if (!cancelled) {
          setAuthNotice(`Email confirmation failed: ${callbackError}`);
          setLoading(false);
        }
        cleanAuthCallbackUrl('/');
        return;
      }

      const { data, error } = await supabase.auth.getSession();
      if (cancelled) return;

      if (error) {
        console.error('Failed to restore auth session', error);
      }

      setSession(data.session);
      setUser(data.session?.user ?? null);

      const callbackType = getAuthCallbackType();
      if (data.session && hasAuthCallbackParams()) {
        if (callbackType === 'recovery') {
          setNeedsPasswordReset(true);
          setAuthNotice('Password reset link verified. Set a new password.');
          // Keep hash until AuthModal finishes reset, then clear.
        } else if (callbackType === 'signup' || callbackType === 'email') {
          setAuthNotice('Email confirmed. You are signed in.');
          cleanAuthCallbackUrl('/dashboard');
        } else if (callbackType === 'magiclink' || callbackType === 'invite') {
          setAuthNotice('You are signed in.');
          cleanAuthCallbackUrl('/dashboard');
        } else {
          setAuthNotice('You are signed in.');
          cleanAuthCallbackUrl('/dashboard');
        }
      }

      setLoading(false);
    };

    void bootstrap();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, nextSession) => {
      setSession(nextSession);
      setUser(nextSession?.user ?? null);
      setLoading(false);

      if (event === 'PASSWORD_RECOVERY') {
        setNeedsPasswordReset(true);
        setAuthNotice('Password reset link verified. Set a new password.');
      }

      if (event === 'SIGNED_IN' && hasAuthCallbackParams()) {
        const callbackType = getAuthCallbackType();
        if (callbackType === 'recovery') {
          setNeedsPasswordReset(true);
          setAuthNotice('Password reset link verified. Set a new password.');
        } else {
          if (callbackType === 'signup' || callbackType === 'email') {
            setAuthNotice('Email confirmed. You are signed in.');
          }
          cleanAuthCallbackUrl('/dashboard');
        }
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  const clearAuthNotice = () => setAuthNotice(null);
  const clearPasswordReset = () => {
    setNeedsPasswordReset(false);
    if (hasAuthCallbackParams()) {
      cleanAuthCallbackUrl('/dashboard');
    }
  };

  const signUp = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({
      email: email.trim().toLowerCase(),
      password,
      options: {
        emailRedirectTo: authRedirectBase(),
      },
    });
    return {
      error,
      session: data.session ?? null,
      user: data.user ?? null,
    };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password,
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
      redirectTo: authRedirectBase(),
    });
    return { error };
  };

  const resendConfirmation = async (email: string) => {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: email.trim().toLowerCase(),
      options: {
        emailRedirectTo: authRedirectBase(),
      },
    });
    return { error };
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        authNotice,
        needsPasswordReset,
        clearAuthNotice,
        clearPasswordReset,
        signUp,
        signIn,
        signOut,
        resetPassword,
        resendConfirmation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
